import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Industries = lazy(() => import("@/pages/Industries"));
const IndustryDetail = lazy(() => import("@/pages/IndustryDetail"));
const Solutions = lazy(() => import("@/pages/Solutions"));
const AIData = lazy(() => import("@/pages/AIData"));
const Insights = lazy(() => import("@/pages/Insights"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Leadership = lazy(() => import("@/pages/Leadership"));
const Careers = lazy(() => import("@/pages/Careers"));
const Legal = lazy(() => import("@/pages/Legal"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));

const ROUTE_SEO = {
  "/services": {
    title: "Technology Consulting Services | ClarityPoint Consulting",
    description: "Explore 13 service lines across digital engineering, AI, cloud, data, integration and DevOps — each with deliverables, outcomes and typical stacks.",
  },
  "/industries": {
    title: "Industry Technology Solutions | ClarityPoint Consulting",
    description: "Technology expertise across banking, healthcare, retail, insurance, telecom, travel, logistics, government and software.",
  },
  "/solutions": {
    title: "Enterprise Solution Architecture | ClarityPoint Consulting",
    description: "Reference architectures, modernization journeys and integration patterns proven in enterprise environments — designed for scale.",
  },
  "/ai-data": {
    title: "AI & Data Solutions for Enterprises | ClarityPoint",
    description: "Production-grade generative AI, RAG, AI agents, machine learning and data platforms — engineered to move from experiment to impact.",
  },
  "/insights": {
    title: "Technology Insights | ClarityPoint Consulting",
    description: "Practitioner perspectives on AI, cloud, architecture, data engineering and digital transformation from the ClarityPoint team.",
  },
  "/about": {
    title: "About ClarityPoint Consulting | Technology Consulting",
    description: "ClarityPoint Consulting is a technology consulting, digital engineering and AI solutions company — engineering the future, one intelligent solution at a time.",
  },
  "/leadership": {
    title: "Leadership | ClarityPoint Consulting",
    description: "Engineering-led leadership focused on client outcomes, architecture excellence and long-term technology partnerships.",
  },
  "/careers": {
    title: "Careers | ClarityPoint Consulting",
    description: "Explore careers at ClarityPoint Consulting — search open roles in engineering, AI, cloud and consulting, or send your resume for future opportunities.",
  },
  "/contact": {
    title: "Contact ClarityPoint Consulting | Start a Conversation",
    description: "Tell us about your technology challenge. An architect reads every inquiry — reach ClarityPoint Consulting in Bethesda, MD.",
  },
  "/admin/login": { title: "Admin Sign In | ClarityPoint Consulting", description: "Administrator access.", noindex: true },
  "/admin/inbox": { title: "Inquiry Inbox | ClarityPoint Consulting", description: "Administrator access.", noindex: true },
};

function RouteSeo() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/services/") || pathname.startsWith("/industries/")) return null;
  const meta = ROUTE_SEO[pathname];
  if (!meta) return null;
  return <Seo title={meta.title} description={meta.description} noindex={meta.noindex || false} />;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          if (lenis) lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView();
        }
      }, 150);
      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const PageLoader = () => (
  <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="page-loader">
    <div className="w-10 h-10 rounded-full border-2 border-cyan-500/20 border-t-cyan-500 animate-spin" />
  </div>
);

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <RouteSeo />
      <div className="bg-ink text-slate-900 min-h-screen font-body">
        <Header />
        <main>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/ai-data" element={<AIData />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/about" element={<About />} />
              <Route path="/leadership" element={<Leadership />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
              <Route path="/terms" element={<Legal doc="terms" />} />
              <Route path="/cookie-policy" element={<Legal doc="cookies" />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/inbox" element={<AdminInbox />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </BrowserRouter>
  );
}
